import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import router from 'next/router'

import { addLocalePrefix } from 'lib/addLocalePrefix'
import SectionLayout from '../../commons/layouts/SectionLayout'

import AreaPrefectureList from './AreaPrefectureList'
import AreaPlaceList from './AreaPlaceList'

// hooks
import { getAreaName } from 'hooks/common/lang'

import styles from './LocationSection.module.scss'
import { LocationFacility, LocationStation } from 'state/ducks/locations/models'
import type { Facility } from 'features/facility/types/facility'

import { Prefecture } from 'state/ducks/prefecture/models'
import {
  PrefectureArea,
  PrefectureInArea,
  PrefecturesResponse,
} from 'features/prefecture/types/prefecture'

interface Props {
  locations: any
  prefectures?: PrefecturesResponse
  isSelectedArea: boolean
  onSelectArea?: (prefecture: PrefectureInArea) => void
  setIsSelectedArea: (isSelectedArea: boolean) => void
  selectedPrefecture?: Prefecture
  stations: LocationStation[] | Facility[]
  facilities: LocationFacility[] | Facility[]
  locale: string
  countryCode: string
  isShowBackButton?: boolean
  isShowAreaName?: boolean
  sectionTitle?: string
  areaPlaceListHeadingStyle?: string
  placeName?: string
}

const LocationSection: React.FC<Props> = ({
  locations,
  prefectures,
  isSelectedArea,
  onSelectArea,
  setIsSelectedArea,
  selectedPrefecture,
  stations,
  facilities,
  locale,
  countryCode = 'jpn',
  isShowBackButton = true,
  isShowAreaName = true,
  sectionTitle,
  areaPlaceListHeadingStyle,
  placeName,
}: Props) => {
  const { t } = useTranslation()
  const getFieldName = (locale: string, item: Prefecture | PrefectureInArea, key: string) => {
    return locale === 'ja' ? item[key] : item[`${locale}_${key}`] ?? item[key]
  }
  const prefectureAreas = prefectures ?? []

  return (
    <SectionLayout
      sectionName="location"
      title={sectionTitle ? sectionTitle : t('TOP.LOCATION.TITLE')}
      titleStyle={styles.title}
    >
      {isSelectedArea ? (
        <div className={styles.selectedArea}>
          {isShowBackButton && (
            <p className={styles.backList}>
              <button
                className={styles.btnBack}
                onClick={() => setIsSelectedArea(false)}
                type="button"
              >
                {t('TOP.LOCATION.BACK_BUTTON_TEXT')}
              </button>
            </p>
          )}
          {isShowAreaName && (
            <h3 className={styles.areaName}>
              <Link href={addLocalePrefix(`/jpn/city/${selectedPrefecture.slug}`, locale)} passHref>
                <a className={styles.areaNameLink}>
                  {t('TOP.LOCATION.PREFECTURE_AREA_LABEL', {
                    prefecture: getFieldName(locale, selectedPrefecture, 'name'),
                  })}
                </a>
              </Link>
            </h3>
          )}
          <AreaPlaceList
            prefectureSlug={selectedPrefecture.slug}
            places={stations}
            label={
              placeName
                ? t('AREAS.CITY.SLUG.LOCATION_SECTION_TITLE_PLACE_STATION_PREFECTURE', {
                    place: placeName,
                  })
                : t('TOP.LOCATION.STATION_LABEL')
            }
            countryCode={countryCode}
            locale={locale}
            headingStyle={areaPlaceListHeadingStyle}
          />
          <div className={styles.stationsContent}>
            <AreaPlaceList
              prefectureSlug={selectedPrefecture.slug}
              places={facilities}
              label={
                placeName
                  ? t('AREAS.CITY.SLUG.LOCATION_SECTION_TITLE_PLACE_FACILITY_PREFECTURE', {
                      place: placeName,
                    })
                  : t('TOP.LOCATION.FACILITY_LABEL')
              }
              countryCode={countryCode}
              locale={locale}
              headingStyle={areaPlaceListHeadingStyle}
            />
          </div>
        </div>
      ) : (
        <>
          {locations?.length > 0 && (
            <ul className={styles.locationsItems}>
              {locations.map((location, index) => (
                <li
                  className={styles.item}
                  style={{
                    background: location.backgroundImage,
                    backgroundSize: 'cover',
                  }}
                  key={index}
                >
                  <div className={styles.itemOverlay}>
                    <Link href={addLocalePrefix(`/jpn/city/${location.slug}`, locale)} passHref>
                      <a className={styles.areaNameLink}>{location.name}</a>
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className={styles.areaItems}>
            {countryCode === 'jpn' &&
              prefectureAreas.length > 0 &&
              prefectureAreas.map((prefecture: PrefectureArea, index) => (
                <div className={styles.areaItem} key={index}>
                  <AreaPrefectureList
                    areaName={getAreaName(locale, prefecture)}
                    prefectures={prefecture.prefectures}
                    onSelectAreaName={onSelectArea}
                    locale={locale}
                    countryCode={countryCode}
                  />
                </div>
              ))}
            {countryCode === 'twn' && prefectureAreas.length > 0 && (
              <div className={styles.gridLocationContainer}>
                {prefectureAreas.map((item, index) => (
                  <div className={styles.location} key={index}>
                    <p className={styles.area}>{item.area_name}</p>
                    <div className={styles.cityContainer}>
                      {(item.prefectures || []).map((prefecture, index) => (
                        <p
                          onClick={() =>
                            router.push(addLocalePrefix(`/twn/city/${prefecture.slug}`, locale))
                          }
                          className={styles.city}
                          key={index}
                        >
                          {prefecture.name}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </SectionLayout>
  )
}

export default LocationSection
